import React, { useMemo } from 'react';
import { Bubble, BubbleProps } from 'tea-component';
import './index.scss';
import { uniqueArray } from '@/pages/opinion/utils';

export default function CompareProgressBubble({
  title,
  startTime,
  endTime,
  name,
  value,
  extraContent,
  placement = 'top',
  showContent = true,
  formatDate = (date: string) => date,
  formatValue = (value: number | string) => value,
  children,
  oldStartTime,
  oldEndTime,
  oldValue,
  showChange = false,
  ...bubbleProps
}: {
  title?: string;
  startTime: string;
  endTime: string;
  name: string | React.ReactNode;
  value: string | number;
  extraContent?: React.ReactNode;
  showContent?: boolean;
  children: any;
  placement?: String;
  formatDate?: (date: string) => string;
  formatValue?: (value: any) => string | number;
  bubbleProps?: React.ForwardRefExoticComponent<
    BubbleProps & React.RefAttributes<HTMLDivElement>
  >;
  oldStartTime?: string;
  oldEndTime?: string;
  oldValue?: number;
  showChange?: boolean;
}) {
  const content = useMemo(() => {
    const start = formatDate?.(startTime) || startTime;
    const end = formatDate?.(endTime) || endTime;
    const displayValue = formatValue?.(value) || value;

    if (showContent) {
      if (showChange) {
        const oldTime = [oldStartTime || '', oldEndTime || ''];
        const newTime = [startTime, endTime];
        const oldTimeText = uniqueArray(oldTime?.map(formatDate))?.join(' - ');
        const newTimeText = uniqueArray(newTime?.map(formatDate))?.join(' - ');

        return (
          <div className="dbo-compare-bubble-box">
            <div className="bubble-box-header">
              <span className="bubble-time-date">{name}</span>
              <span className="bubble-title">{title}</span>
            </div>
            <div className="item-content">
              <div className="item-date-box">
                <div className="item-date">{newTimeText}</div>
                <div className="item-date">{oldTimeText} </div>
              </div>
              <div className="item-vs">VS</div>
              <div className="item-num-box">
                <div className="item-num">{displayValue}</div>
                <div className="item-num">
                  {formatValue?.(oldValue) || oldValue}
                </div>
              </div>
            </div>
            {extraContent && (
              <div className="bubble-box-footer">{extraContent}</div>
            )}
          </div>
        );
      }

      return (
        <div className="dbo-compare-bubble-box">
          <div className="bubble-box-header">
            <span className="bubble-time-date">
              {start}~{end}
            </span>
            <span className="bubble-title">{title}</span>
          </div>
          <div className="bubble-box-body">
            <span>{name}</span>
            <span className="bubble-value">{displayValue}</span>
          </div>
          {extraContent && (
            <div className="bubble-box-footer">{extraContent}</div>
          )}
        </div>
      );
    }

    return null;
  }, [
    formatDate,
    startTime,
    endTime,
    formatValue,
    value,
    showContent,
    title,
    name,
    extraContent,
  ]);

  return (
    <Bubble
      dark
      className="ring-ratio-bubble-wrapper"
      overlayClassName={'ring-ration-bubble-box'}
      placement={placement as any}
      content={content}
      closeOnScroll
      {...bubbleProps}
    >
      <span className="child-item">{children}</span>
    </Bubble>
  );
}
